import React, { useState, useEffect } from "react";
import { collection, getDocs, doc, setDoc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../../firebase";
import AdminShell from "../../components/AdminShell";

const FILE_CATEGORIES = ["DTI Certificate", "Business Permit", "Company Profile", "Brochure", "Certification", "SEC Registration", "BIR Certificate", "Other"];

export default function AdminFiles() {
  const [hirings, setHirings] = useState([]);
  const [selectedJob, setSelectedJob] = useState("");
  const [files, setFiles] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState("");
  const [uploadCategory, setUploadCategory] = useState(FILE_CATEGORIES[0]);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    getDocs(collection(db, "hirings")).then((snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setHirings(list);
      if (list.length > 0) setSelectedJob(list[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selectedJob) return;
    loadFiles(selectedJob);
  }, [selectedJob]);

  const loadFiles = async (jobId) => {
    try {
      const snap = await getDoc(doc(db, "job_files", jobId));
      setFiles(snap.exists() ? (snap.data().files || []) : []);
    } catch (e) { console.error(e); setFiles([]); }
  };

  const upload = async () => {
    if (!uploadFile || !uploadName.trim()) { setMsg("File and name required."); return; }
    setUploading(true); setMsg("");
    try {
      const path = `job_files/${selectedJob}/${Date.now()}_${uploadFile.name}`;
      const snap = await uploadBytes(ref(storage, path), uploadFile);
      const url = await getDownloadURL(snap.ref);
      const newFile = { id: Date.now().toString(), name: uploadName.trim(), category: uploadCategory, url, storagePath: path, uploadedAt: new Date().toISOString() };
      const updated = [...files, newFile];
      await setDoc(doc(db, "job_files", selectedJob), { jobId: selectedJob, files: updated, updatedAt: serverTimestamp() }, { merge: true });
      setFiles(updated);
      setUploadFile(null); setUploadName(""); setUploadCategory(FILE_CATEGORIES[0]);
      setMsg("File uploaded.");
    } catch (e) { setMsg("Upload failed: " + e.message); }
    setUploading(false);
  };

  const removeFile = async (fileItem) => {
    if (!window.confirm(`Remove "${fileItem.name}"?`)) return;
    try {
      if (fileItem.storagePath) { try { await deleteObject(ref(storage, fileItem.storagePath)); } catch {} }
      const updated = files.filter((f) => f.id !== fileItem.id);
      await updateDoc(doc(db, "job_files", selectedJob), { files: updated });
      setFiles(updated);
    } catch (e) { setMsg("Error: " + e.message); }
  };

  const currentHiring = hirings.find((h) => h.id === selectedJob);
  const grouped = FILE_CATEGORIES.reduce((acc, cat) => {
    acc[cat] = files.filter((f) => f.category === cat);
    return acc;
  }, {});

  return (
    <AdminShell title="File Organizer">
      <div className="alert alert-info" style={{ marginBottom: 16 }}>
        <i className="ti ti-info-circle"></i> Upload documents (DTI Cert, Brochure, Company Profile, etc.) per job. These are available when generating applicant documents for that job.
      </div>
      {msg && <div className="alert alert-info">{msg}</div>}

      {/* Job selector */}
      <div className="card mb-24">
        <div className="flex-between" style={{ flexWrap: "wrap", gap: 12 }}>
          <div>
            <label className="form-label">Select Job / Hiring</label>
            <select className="form-control" style={{ minWidth: 280 }} value={selectedJob} onChange={(e) => setSelectedJob(e.target.value)}>
              {hirings.map((h) => <option key={h.id} value={h.id}>{h.flag} {h.title} — {h.country}</option>)}
            </select>
          </div>
          {currentHiring && (
            <div style={{ fontSize: 13, color: "var(--ink-500)" }}>
              <strong>{files.length}</strong> file{files.length !== 1 ? "s" : ""} uploaded for this job
            </div>
          )}
        </div>
      </div>

      {selectedJob && (
        <>
          {/* Upload form */}
          <div className="card mb-24">
            <h3 style={{ fontSize: 16, marginBottom: 16 }}><i className="ti ti-upload"></i> Upload New Document</h3>
            <div className="form-row cols-2">
              <div className="form-group">
                <label className="form-label">Document Name *</label>
                <input className="form-control" value={uploadName} onChange={(e) => setUploadName(e.target.value)} placeholder="e.g., DTI Certificate 2024" />
              </div>
              <div className="form-group">
                <label className="form-label">Category</label>
                <select className="form-control" value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value)}>
                  {FILE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">File *</label>
              <input type="file" className="form-control" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => setUploadFile(e.target.files[0])} />
              <small className="text-muted">PDF, JPG, PNG, DOC up to 10MB</small>
            </div>
            <button className="btn btn-primary" onClick={upload} disabled={uploading || !uploadFile || !uploadName.trim()}>
              {uploading ? <span className="spinner"></span> : <><i className="ti ti-upload"></i> Upload</>}
            </button>
          </div>

          {/* Files by category */}
          <div className="card">
            <h3 style={{ fontSize: 16, marginBottom: 16 }}><i className="ti ti-folder-open"></i> Documents — {currentHiring?.title}</h3>
            {files.length === 0 ? (
              <div className="empty-state"><i className="ti ti-file-off"></i><div>No documents uploaded yet</div></div>
            ) : (
              FILE_CATEGORIES.map((cat) => grouped[cat].length > 0 && (
                <div key={cat} style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--gold-600)", marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid var(--ink-100)" }}>
                    {cat}
                  </div>
                  {grouped[cat].map((f) => (
                    <div key={f.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", border: "1px solid var(--ink-200)", borderRadius: 8, marginBottom: 6 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <i className="ti ti-file" style={{ fontSize: 20, color: "var(--gold-500)" }}></i>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{f.name}</div>
                          <div style={{ fontSize: 12, color: "var(--ink-400)" }}>{new Date(f.uploadedAt).toLocaleDateString()}</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <a href={f.url} target="_blank" rel="noreferrer" className="btn btn-outline" style={{ padding: "4px 10px", fontSize: 12 }}><i className="ti ti-eye"></i> View</a>
                        <button className="btn btn-danger" style={{ padding: "4px 10px", fontSize: 12 }} onClick={() => removeFile(f)}><i className="ti ti-trash"></i></button>
                      </div>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </>
      )}
    </AdminShell>
  );
}
